import { keepPreviousData, useQuery } from '@tanstack/react-query'
import cx from 'classnames'
import momentTZ from 'moment-timezone'
import moment from 'moment/moment'
import toast from 'react-hot-toast'
import { LuAlertTriangle } from 'react-icons/lu'
import { useNavigate, useParams } from 'react-router-dom'
import { useCopyToClipboard } from 'react-use'
import stateMapping from 'shared_src/dictionaries/usStateCodes'

import http from '../../../../lib/http'
import { capitalize } from '../../../igao/utils'

function useTimeZone (id) {
  return useQuery({
    queryKey: [ 'timezone', id ],
    async queryFn () {
      const response = await http.getRequest(`/patients/${id}/timezone`)
      if (response.timezone) {
        console.log(momentTZ().tz(`US/${response.timezone}`).zoneAbbr())
        return { ...response, currentTime: momentTZ().tz(`US/${response.timezone}`).format('HH:mm'), abbr: momentTZ().tz(`US/${response.timezone}`).zoneAbbr() }
      }
      return null
    },
    refetchOnWindowFocus: false
  })
}

const parToName = {
  1: 'green',
  2: 'green',
  3: 'yellow',
  4: 'orange',
  5: 'red'
}

const severityToName = {
  0: 'white',
  1: 'green',
  2: 'yellow',
  3: 'orange',
  4: 'red'
}

const Section1 = ({
  name,
  age,
  gender,
  dob,
  ssn,
  par,
  patientState,
  isUrgent,
  status,
  lastStatusUpdateDate
}) => {
  const [ , copy ] = useCopyToClipboard()
  const navigate = useNavigate()
  const params = useParams()

  let amountsQuery = useQuery({
    queryKey: [
      'claims-history/amounts',
      params.id
    ],
    queryFn: async function getAmounts ({ queryKey }) {
      let [ , resource ] = queryKey
      let response = await http.getRequest(`/patients/${resource}/claims/totals`)

      return {
        ...response,
        amounts: response.amounts
          .slice()
          .sort((a, b) => parseInt(b.year, 10) - parseInt(a.year, 10))
      }
    },
    enabled: typeof params.id === 'string',
    retry: false,
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
    refetchOnReconnect: true,
    refetchOnWindowFocus: false
  })

  function goToPatientFolder () {
    const { id, clientName } = params
    if (!id || !clientName) {
      return
    }
    const selection = window.getSelection()

    if (selection.toString().trim().length > 0) {
      return
    }

    navigate(`/patient/${clientName}/${id}/patientFolder`)
  }

  function copyName (e) {
    e.stopPropagation()
    copy(name ?? '')
    toast.success('Patient\'s name copied to clipboard', { position: 'bottom-center', duration: 5000 })
  }

  return (
    <div className='tailwind p-2'>
      <button
        className='flex flex-row justify-between py-3 px-5 min-w-[15.8rem] rounded-md bg-[#F1F2F4] hover:bg-[#EBECED] transition-colors select-text'
        onClick={goToPatientFolder}
      >
        <div className='flex flex-col gap-2'>
          <button
            onClick={copyName}
            title={'Click to copy patient\'s name'}
          >
            <h1 className='bold patient-name text-base tracking-wide'>{name}</h1>
          </button>
          <div className='flex flex-row gap-0.5'>
            <span
              className='bold'
              title='Age, years'
            >
              {age}
            </span>
            <span title='Gender'>{gender}</span>
            <span title='Date of Birth'>{dob}</span>
          </div>
          <div title='SSN'>
            <span>{ssn?.slice(0, ssn.length - 4) ?? ''}</span>
            <span className='font-semibold'>{ssn?.slice(ssn.length - 4) ?? ''}</span>
          </div>
          <div title='Enrollment Status'>
            <span className='font-semibold'>{status}</span>{' '}
            <span className='font-inter text-gray-500'>{moment(lastStatusUpdateDate).fromNow(true)}</span>
          </div>
        </div>
        <div className='flex flex-col justify-between items-end'>
          {par
            ? (
              <div className='flex flex-col items-center gap-2'>
                <div
                  className={cx('py-0.5 px-3 w-full text-xs font-bold whitespace-nowrap', {
                    'bg-[#ECFDF5] text-[#059669]': parToName[par] === 'green',
                    'bg-[#FEF9C3] text-[#CA8A04]': parToName[par] === 'yellow',
                    'bg-[#FF740035] text-[#FF7400]': parToName[par] === 'orange',
                    'bg-[#FEE2E2] text-[#DC2626]': parToName[par] === 'red'
                  })}
                  title='Patient Acuity Rating'
                >
                  PAR{' '}{par}
                </div>

                <div
                  className={cx('py-0.5 px-3 w-full text-xs text-center font-bold whitespace-nowrap', {
                    'bg-[#fff] text-[#000]': severityToName[amountsQuery?.data?.severity || 0] === 'white',
                    'bg-[#C8DE8E] text-[#5C940F]': severityToName[amountsQuery?.data?.severity] === 'green',
                    'bg-[#FFD481] text-[#9D7100]': severityToName[amountsQuery?.data?.severity] === 'yellow',
                    'bg-[#F99566] text-[#9F3401]': severityToName[amountsQuery?.data?.severity] === 'orange',
                    'bg-[#FF9A94] text-[#D1002A]': severityToName[amountsQuery?.data?.severity] === 'red'
                  })}
                  title='Severity'
                >
                  Sev {amountsQuery?.data?.severity || 0}
                </div>

                {isUrgent && (
                  <div
                    title='Urgent IGAO'
                    className=''
                  >
                    <LuAlertTriangle
                      size='1.2rem'
                      color='#F2545A'
                    />
                  </div>
                )}

              </div>
            )
            : <div />}
          <StateAndTimeZone
            patientId={params.id}
            patientState={patientState}
          />
        </div>
      </button>
    </div>
  )
}

function StateAndTimeZone ({ patientId, patientState }) {
  const { data: timezone } = useTimeZone(patientId)

  const fullStateName = stateMapping.find((s) => s.code === patientState)?.label

  const stateName = fullStateName ? fullStateName?.toLowerCase()?.split(' ')?.map((word) => capitalize(word))?.join(' ') : patientState

  let tooltip = `State: ${stateName}`

  if (timezone?.timezone) {
    tooltip += `\nPatient time zone: ${timezone?.timezone} ${timezone?.currentTime}`
  }

  return (
    <div
      className='py-0.5 px-3 flex gap-[0.2rem] bg-[#E0F8A7] text-center'
      title={tooltip}
    >
      <span
        className='text-[0.8rem] text-[#47556D] font-black font-roboto'
      >
        {patientState}
      </span>
      {timezone && (
        <span
          className='text-[0.8rem] font-light text-[#47556D]'
        >
          {timezone?.abbr}
        </span>
      )}

    </div>
  )
}

export default Section1
